import type { ButtonOptions, OpenResult, TransactionResult, UtmParam, WeGivePayConfig } from './types'
import { detectStrategy } from './platform'
import { resolveEnvironment, resolvePopupUrl } from './environment'
import { serializePopupQuery } from './protocol'
import { resolveUtmSet } from './utm'

/** Open the popup, return a promise that resolves when the donor finishes
 *  (success), cancels (closes the window), or errors out. */
export function openPopup(config: WeGivePayConfig, opts: ButtonOptions): Promise<OpenResult> {
  const url = buildUrl(config, opts)
  // A mobile tab retains its opener and the same result protocol. Embedded
  // browsers that cannot open a tab receive a recoverable error.
  return openDesktopWindow(url, opts, detectStrategy() === 'desktop-window')
}

/** Exported for unit testing. Composes the popup URL including `parent_origin`,
 *  which the popup forwards to `POST /api/pay/sessions` so the API can verify
 *  the embedder origin against the PK's allow-list. */
export function buildUrl(config: WeGivePayConfig, opts: ButtonOptions): string {
  const base = resolvePopupUrl(config)
  const params = serializePopupQuery({
    ...opts,
    publishableKey: config.publishableKey,
    environment: resolveEnvironment(config),
    parentOrigin:
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : undefined,
    trackingUrl: opts.trackingUrl ?? currentHref(),
    utmParams: resolveUtmParams(opts),
  })
  return `${base}/checkout?${params.toString()}`
}

function currentHref(): string | undefined {
  if (typeof window === 'undefined') return undefined
  return window.location?.href || undefined
}

/**
 * The effective UTM set for this checkout (WEG-7657).
 *
 * Precedence: the embedder's explicit `opts.utmParams`, otherwise the live page URL.
 * Whichever has values wins as a whole set — the two are never merged.
 *
 * The SDK reads no cookies. An embedder that persists attribution on its own domain
 * passes the stored set as `opts.utmParams`, where it takes precedence.
 *
 * Resolution has to happen here on the parent page regardless: the popup at
 * `pay.wegive.com` is cross-site to the embedder and can never see its URL or cookies.
 */
function resolveUtmParams(opts: ButtonOptions): Partial<Record<UtmParam, string>> {
  return resolveUtmSet(opts.utmParams ?? null)
}

function openDesktopWindow(url: string, opts: ButtonOptions, sized: boolean): Promise<OpenResult> {
  const width = 480
  const height = 720
  const left = (window.screen.width - width) / 2 + (window.screenX || 0)
  const top = (window.screen.height - height) / 2 + (window.screenY || 0)
  const features = `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`
  const win = window.open(url, `wegive-pay-${crypto.randomUUID()}`, sized ? features : undefined)

  if (!win) {
    const error = new Error('Checkout could not open. Allow pop-ups or open this page in your browser, then try again.')
    try { opts.onError?.(error) } catch { /* Host callbacks must not prevent settlement. */ }
    return Promise.resolve({ status: 'error', error: error.message })
  }

  const expectedOrigin = new URL(url).origin

  return new Promise<OpenResult>((resolve) => {
    let resolved = false
    const finish = (result: OpenResult, callback?: () => void) => {
      if (resolved) return
      resolved = true
      window.removeEventListener('message', onMessage)
      clearInterval(pollClose)
      try {
        win.close()
      } catch {}
      resolve(result)
      try { callback?.() } catch { /* Host callbacks must not prevent settlement. */ }
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin || event.source !== win) return
      const data = event.data as { type?: string; transaction?: TransactionResult; scheduledDonationId?: number; status?: string } | undefined
      if (!data || typeof data.type !== 'string') return
      if (!data.type.startsWith('wegive:')) return
      switch (data.type) {
        case 'wegive:popup-ready':
          // Handshake: tell the popup our origin so it can verify event.origin
          // against the parent_origin claim. Send targeted at the popup's
          // origin so a malicious tab can't intercept by name.
          try {
            win.postMessage(
              { type: 'wegive:handshake', parentOrigin: window.location.origin },
              expectedOrigin,
            )
          } catch {}
          break
        case 'wegive:success':
        case 'wegive:pending':
        case 'wegive:scheduled':
        case 'wegive:complete': {
          const status = data.type === 'wegive:complete' ? data.status ?? 'success' : data.type.slice(7)
          if (status === 'scheduled' && data.scheduledDonationId) {
            finish({ status: 'scheduled', scheduledDonationId: data.scheduledDonationId }, () => opts.onScheduled?.(data.scheduledDonationId!))
          } else if (status === 'pending' && data.transaction) {
            finish({ status: 'pending', transaction: data.transaction, ...(data.scheduledDonationId ? { scheduledDonationId: data.scheduledDonationId } : {}) }, () => opts.onPending?.(data.transaction!))
          } else if (status === 'success' && data.transaction) {
            finish({ status: 'success', transaction: data.transaction, ...(data.scheduledDonationId ? { scheduledDonationId: data.scheduledDonationId } : {}) }, () => opts.onSuccess?.(data.transaction!))
          }
          break
        }
        case 'wegive:cancel':
          finish({ status: 'cancel' }, opts.onCancel)
          break
        case 'wegive:error':
          {
            const error = new Error((data as { message?: string }).message ?? 'Unknown error')
            finish({ status: 'error', error: error.message }, () => opts.onError?.(error))
          }
          break
      }
    }

    window.addEventListener('message', onMessage)

    // Detect manual close.
    const pollClose = window.setInterval(() => {
      if (win.closed) {
        finish({ status: 'cancel' }, opts.onCancel)
      }
    }, 500)
  })
}
